import React, { useState } from "react";
import Head from "next/head";
import { GetPost } from "@graphql/queries";
import PostContainer from "@components/postComponents/postMedia/PostContainer";
import PostDetailsContainer from "@components/postComponents/postDetails/PostDetailsContainer";
import {
  generatePostImageThumbUrl,
  generatePostVideoThumbnailUrl,
  generatePostVideoUrl,
  generatePostDrawingUrl,
} from "@utility/index";
import { ContentType } from "@utility/constants";
import NoPostFoundScreen from "@components/postComponents/NoPostFoundScreen";
import graphqlClient from "@graphql/apollo-client";
import { AmplifyConfigure } from "@utility/amplify";
import DownloadTheAppModal from "@components/modals/DownloadTheAppModal";
import ShareLinksModal from "@components/modals/ShareLinksModal";
import Layout from "@components/Layout";
import Topbar from "@components/postComponents/postMedia/TopBar";

export default function Index({ metaTags, post }) {
  const [downloadAppModalOpen, setDownloadAppModalOpen] = useState(false);
  const [shareLinksModal, setShareLinksModal] = useState(false);

  if (!post) return <NoPostFoundScreen />;
  const postUrl = `${process.env.NEXT_PUBLIC_SNACKTIME_PUBLIC_WEB_URL}/post/${post.id}`;
  return (
    <>
      <Head>
        <meta property="og:url" content={postUrl} key="ogurl" />
        <meta property="og:title" content={metaTags.title} key="ogtitle" />
        <meta property="og:site_name" content="Snacktime" key="ogsitename" />
        <meta
          property="og:description"
          content={metaTags.description}
          key="ogdesc"
        />
        <meta
          property="og:image"
          itemProp="image"
          content={metaTags.thumbnailUrl}
          key="ogimage"
        />
        <meta
          property="og:image:alt"
          content={metaTags.description}
          key="ogimagealt"
        />
        <meta property="og:type" content="website" key="ogtype" />
        <meta
          name="twitter:site"
          content={process.env.NEXT_PUBLIC_SNACKTIME_TWITTER_USERNAME}
        />
        <meta name="twitter:title" content={metaTags.title} />
        <meta name="twitter:description" content={metaTags.description} />
        <meta name="twitter:image" content={metaTags.thumbnailUrl} />
        <meta name="twitter:image:alt" content={metaTags.description} />
        <meta
          property="fb:app_id"
          content={process.env.NEXT_PUBLIC_FACEBOOK_APP_ID}
        />
        {post.contentType === ContentType.IMAGE ? (
          <meta name="twitter:card" content="summary_large_image" />
        ) : (
          <>
            <meta
              property="og:video"
              content={`${process.env.NEXT_PUBLIC_SNACKTIME_APPLINK_URL}/p/${post.id}`}
              key="ogvideo"
            />
            <meta
              name="twitter:player"
              content={`${process.env.NEXT_PUBLIC_SNACKTIME_APPLINK_URL}/p/${post.id}`}
            />
            <meta name="twitter:card" content="player" />
            <meta name="twitter:player:width" content={post.contentWidth} />
            <meta name="twitter:player:height" content={post.contentHeight} />
          </>
        )}
      </Head>
      {/* MODALS START */}
      <DownloadTheAppModal
        setDownloadAppModalOpen={setDownloadAppModalOpen}
        downloadAppModalOpen={downloadAppModalOpen}
      />
      <ShareLinksModal
        title="Share this snack"
        shareUrl={`${process.env.NEXT_PUBLIC_SNACKTIME_APPLINK_URL}/post/${post.id}`}
        setShareLinksModal={setShareLinksModal}
        shareLinksModal={shareLinksModal}
      />
      {/* MODALS END */}
      <div className="hidden lg:flex">
        <Topbar />
      </div>

      <Layout
        title={
          post.title
            ? `${post.user.name} on Snacktime: ${
                post.title.slice(0, 60) + (post.title.length > 60 ? "..." : "")
              } by @${post.user.username}`
            : `${post.user.name} on Snacktime`
        }
      >
        <div className="w-full flex flex-row max-h-screen h-full lg:min-h-screen">
          <PostContainer post={post} />
          <PostDetailsContainer
            post={post}
            setDownloadAppModalOpen={setDownloadAppModalOpen}
            setShareLinksModal={setShareLinksModal}
            shareLinksModal={shareLinksModal}
          />
        </div>
      </Layout>
    </>
  );
}

export async function getServerSideProps({ res, params }) {
  const { postId } = params;
  AmplifyConfigure();
  const id = postId;
  const client = graphqlClient;

  const { data } = await client.query({
    query: GetPost,
    variables: { id },
  });

  const post = data.getPost;

  // If no post set 404 in the browser and show no post results screen.
  if (!post) {
    res.statusCode = 404;
    return {
      props: {
        post: null,
      },
    };
  }

  const { user } = post;
  const playerUrl =
    post?.contentType === "VIDEO" ? generatePostVideoUrl({ post }) : "";

  let thumbnailUrl = "";
  switch (post?.contentType) {
    case ContentType.IMAGE:
      thumbnailUrl = generatePostImageThumbUrl({
        post,
        edits: {
          smartCrop: {
            faceIndex: 0, // zero-based index of detected faces
            padding: 150, // padding expressed in pixels, applied to all sides
          },
          resize: {
            width: 1200,
            height: 630,
            fit: "cover",
            strategy: "attention",
          },
        },
      });
      break;
    case ContentType.VIDEO:
      thumbnailUrl = generatePostVideoThumbnailUrl({ post });
      break;
    case ContentType.DRAWING:
      thumbnailUrl = generatePostDrawingUrl({ post });
      break;
    default:
  }

  const url = `${process.env.NEXT_PUBLIC_SNACKTIME_PUBLIC_WEB_URL}/post/${post.id}`;

  const metaTags = {
    thumbnailUrl,
    playerUrl: playerUrl || "",
    url,
    title: `@${user?.username}'s Snack on Snacktime`,
    description: post?.title || "",
  };

  return {
    props: {
      metaTags,
      post,
    },
  };
}
